import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_LANDING_PAGE_TITLE = 200;
/** “Total marks per right answer” — admin input and saved `points` */
const MAX_RIGHT_ANSWER_POINTS = 100;
const MAX_QUESTIONS = 20;
const TIMER_MIN_SECONDS = 5;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

function parseStoredBool(raw, fallback = true) {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;
  return fallback;
}

/** Digits only; no negatives, no letters. Empty while typing; 0 and below become "". Capped at max. */
const normalizeMarksInput = (raw, max) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1) return "";
  if (n > max) return String(max);
  return String(n);
};

const MINUTE_OPTIONS = Array.from({ length: 16 }, (_, i) => i);
const SECOND_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([""]);
  const [points, setPoints] = useState("1");
  const [totalQuestion, setTotalQuestion] = useState("10");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [buttonTextColor, setButtonTextColor] = useState("#000000");
  const [textColor, setTextColor] = useState("#000000");
  const [iconBgColor, setIconBgColor] = useState("#ffffff");
  const [boxColor, setBoxColor] = useState("#1e293b");
  const [useTimeoutEnabled, setUseTimeoutEnabled] = useState(true);
  const [timeoutMinutes, setTimeoutMinutes] = useState("1");
  const [timeoutSeconds, setTimeoutSeconds] = useState("30");
  const [landingPageTitle, setLandingPageTitle] = useState("");

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;

    const r = ensureArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);

    setPoints(String(clampInt(data.points, 1, MAX_RIGHT_ANSWER_POINTS, 1)));
    setTotalQuestion(String(clampInt(data.total_question, 1, MAX_QUESTIONS, 10)));
    setButtonColor(toColorInputValue(data.button_color));
    setButtonTextColor(toColorInputValue(data.button_Textcolor ?? data.button_text_color));
    setTextColor(toColorInputValue(data.textcolor));
    setIconBgColor(toColorInputValue(data.icon_bg_color));
    setBoxColor(toColorInputValue(data.box_color ?? data.boxcolor ?? "#1e293b"));
    setUseTimeoutEnabled(parseStoredBool(data.use_timeout, true));

    const m = clampInt(data.question_timeout_minutes, 0, 59, 1);
    const s = clampInt(data.question_timeout_seconds, 0, 59, 30);
    setTimeoutMinutes(String(m));
    setTimeoutSeconds(String(s));
    setLandingPageTitle(String(data.landing_page_title ?? "").trim());
  }, [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > MAX_RULE_LEN) {
      Swal.fire("Limit", `Each rule can be at most ${MAX_RULE_LEN} characters.`, "warning");
      val = val.slice(0, MAX_RULE_LEN);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmed = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmed.length === 0) return { ok: false, msg: "At least one rule is required." };
    for (const r of trimmed) {
      if (r.length > MAX_RULE_LEN) return { ok: false, msg: "Each rule must be 200 characters or less." };
    }

    const tq = clampInt(totalQuestion, 1, MAX_QUESTIONS, 10);
    if (!Number.isFinite(tq) || tq < 1) return { ok: false, msg: "Total questions must be at least 1." };
    if (tq > MAX_QUESTIONS) return { ok: false, msg: `Maximum ${MAX_QUESTIONS} questions.` };

    if (useTimeoutEnabled) {
      const m = clampInt(timeoutMinutes, 0, 59, 0);
      const sec = clampInt(timeoutSeconds, 0, 59, 0);
      const totalSec = m * 60 + sec;
      if (totalSec < TIMER_MIN_SECONDS) {
        return {
          ok: false,
          msg: `Question timer must be at least ${TIMER_MIN_SECONDS} seconds total (minutes + seconds).`,
        };
      }
    }

    const lt = landingPageTitle.trim();
    if (lt.length > MAX_LANDING_PAGE_TITLE) {
      return { ok: false, msg: `Landing page title must be ${MAX_LANDING_PAGE_TITLE} characters or less.` };
    }

    const mSave = useTimeoutEnabled ? clampInt(timeoutMinutes, 0, 59, 0) : 0;
    const secSave = useTimeoutEnabled ? clampInt(timeoutSeconds, 0, 59, 0) : 0;

    return {
      ok: true,
      payload: {
        landing_page_title: lt,
        custom_text_thank_you_page: String(data?.custom_text_thank_you_page ?? "").trim(),
        rules: trimmed,
        points: clampInt(points, 1, MAX_RIGHT_ANSWER_POINTS, 1),
        wrong_points: clampInt(data?.wrong_points, 1, Number.MAX_SAFE_INTEGER, 1),
        total_question: tq,
        button_color: buttonColor,
        button_Textcolor: buttonTextColor,
        textcolor: textColor,
        icon_bg_color: iconBgColor,
        box_color: boxColor,
        use_timeout: useTimeoutEnabled,
        question_timeout_minutes: mSave,
        question_timeout_seconds: secSave,
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data updated", "success");
      navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout">
        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Add Custom Rules</h4>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Rules"
                      maxLength={MAX_RULE_LEN}
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="themeupdate-muted">
                  <code>
                    * Up to {MAX_RULES} rules, at least 1 required, {MAX_RULE_LEN} characters each
                  </code>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Page Content</h4>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-landing-title" className="rules-php-marks-label">
                  Landing page title (shown above game timer)
                </label>
                <input
                  id="rules-landing-title"
                  type="text"
                  className="themeupdate-input rules-php-number"
                  style={{ maxWidth: "100%", width: "100%" }}
                  maxLength={MAX_LANDING_PAGE_TITLE}
                  value={landingPageTitle}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length > MAX_LANDING_PAGE_TITLE) {
                      Swal.fire("Limit", `Landing page title max ${MAX_LANDING_PAGE_TITLE} characters.`, "warning");
                      setLandingPageTitle(v.slice(0, MAX_LANDING_PAGE_TITLE));
                    } else {
                      setLandingPageTitle(v);
                    }
                  }}
                  placeholder="Title"
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-points" className="rules-php-marks-label">
                  Total marks per right answer (max {MAX_RIGHT_ANSWER_POINTS})
                </label>
                <input
                  id="rules-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  className="themeupdate-input rules-php-number"
                  value={points}
                  onChange={(e) => setPoints(normalizeMarksInput(e.target.value, MAX_RIGHT_ANSWER_POINTS))}
                  onBlur={() => {
                    const n = parseInt(points, 10);
                    if (points === "" || Number.isNaN(n) || n < 1) setPoints("1");
                    else setPoints(String(clampInt(n, 1, MAX_RIGHT_ANSWER_POINTS, 1)));
                  }}
                />
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-total-question" className="rules-php-marks-label">
                  Total questions (max {MAX_QUESTIONS})
                </label>
                <input
                  id="rules-total-question"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="themeupdate-input rules-php-number"
                  value={totalQuestion}
                  onChange={(e) => setTotalQuestion(normalizeMarksInput(e.target.value, MAX_QUESTIONS))}
                  onBlur={() => {
                    const n = parseInt(totalQuestion, 10);
                    if (totalQuestion === "" || Number.isNaN(n) || n < 1) setTotalQuestion("1");
                    else setTotalQuestion(String(clampInt(n, 1, MAX_QUESTIONS, 10)));
                  }}
                />
              </div>

              <div className="rules-php-toggle-row rules-php-marks-row">
                <label className="rules-php-marks-label" htmlFor="rules-use-timeout">
                  Question timer ({useTimeoutEnabled ? "on" : "off"})
                </label>
                <button
                  type="button"
                  id="rules-use-timeout"
                  className={`rules-timeout-toggle ${useTimeoutEnabled ? "is-on" : ""}`}
                  onClick={() => setUseTimeoutEnabled((v) => !v)}
                  aria-pressed={useTimeoutEnabled}
                >
                  {useTimeoutEnabled ? "On" : "Off"}
                </button>
              </div>

              {useTimeoutEnabled ? (
                <div className="rules-timeout-pickers">
                  <div className="rules-php-marks-row">
                    <label htmlFor="rules-timeout-mm" className="rules-php-marks-label">
                      Timer minutes
                    </label>
                    <select
                      id="rules-timeout-mm"
                      className="themeupdate-input rules-php-select"
                      value={timeoutMinutes}
                      onChange={(e) => setTimeoutMinutes(e.target.value)}
                    >
                      {MINUTE_OPTIONS.map((n) => (
                        <option key={n} value={String(n)}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="rules-php-marks-row">
                    <label htmlFor="rules-timeout-ss" className="rules-php-marks-label">
                      Timer seconds
                    </label>
                    <select
                      id="rules-timeout-ss"
                      className="themeupdate-input rules-php-select"
                      value={timeoutSeconds}
                      onChange={(e) => setTimeoutSeconds(e.target.value)}
                    >
                      {SECOND_OPTIONS.map((n) => (
                        <option key={n} value={String(n)}>
                          {String(n).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <p className="themeupdate-muted rules-timeout-off-note">
                  <code>Timed questions are disabled. Players see question number only.</code>
                </p>
              )}

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-btn-color" className="rules-php-marks-label">
                  Button color
                </label>
                <input
                  id="rules-btn-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonColor}
                  onChange={(e) => setButtonColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-btn-text" className="rules-php-marks-label">
                  Button text color
                </label>
                <input
                  id="rules-btn-text"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonTextColor}
                  onChange={(e) => setButtonTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-text-color" className="rules-php-marks-label">
                  Text color
                </label>
                <input
                  id="rules-text-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-icon-bg-color" className="rules-php-marks-label">
                  Icon background color
                </label>
                <input
                  id="rules-icon-bg-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={iconBgColor}
                  onChange={(e) => setIconBgColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-box-color" className="rules-php-marks-label">
                  Box color
                </label>
                <input
                  id="rules-box-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={boxColor}
                  onChange={(e) => setBoxColor(e.target.value)}
                />
              </div>
              <p className="themeupdate-muted">
                <code>Panels behind question image and popups.</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={loading}>
          Save
        </button>
      </div>
    </>
  );
};

export default Rules;
